import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import ContactForm from './ContactForm';

const Contact = () => {
  const openWhatsApp = () => {
    const message = encodeURIComponent("Hi, I'm interested in your IT services. Can we discuss?");
    window.open(`https://wa.me/${companyInfo.whatsapp.replace(/\+|\s/g, '')}?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-16 lg:py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-gray-600">
            Ready to transform your business with technology? Let's start a conversation
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto items-start">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-blue-50/50 p-8 rounded-2xl border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm text-blue-900 shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-1">Email Us</p>
                    <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-blue-900 transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm text-blue-900 shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-1">Call Us</p>
                    <a href={`tel:${companyInfo.phone}`} className="text-gray-600 hover:text-blue-900 transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm text-blue-900 shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-1">Visit Us</p>
                    <p className="text-gray-600 leading-relaxed">{companyInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white shadow-lg relative overflow-hidden group">
              <div className="relative z-10">
                <MessageSquare className="mb-4 text-blue-100" size={32} />
                <h4 className="font-bold text-xl mb-2">Quick Chat?</h4>
                <p className="text-blue-50 mb-6">Get instant support and quick answers via WhatsApp.</p>
                <Button 
                  onClick={openWhatsApp}
                  className="w-full bg-white text-blue-600 hover:bg-blue-50 font-semibold border-0"
                >
                  Chat on WhatsApp
                </Button>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full transform translate-x-10 -translate-y-10 group-hover:scale-110 transition-transform duration-500"></div>
            </div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <ContactForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
